<template>
    <div class="dosage-page">
        <van-list
            v-model="loading"
            :finished="finished"
            finished-text="没有更多了"
            @load="onLoad"
        >
            <div v-if="dosageList.length" class="list-wrap">
                <div class="dosage-card" v-for="item in dosageList" :key="item.rowKey">
                    <div class="main-row">
                        <div class="left-user">
                            <van-icon name="todo-list-o" color="#46ccdc" size="18" />
                            <span class="name">{{ item.name }}</span>
                            <span v-if="item.pending" class="pending-tag">待检测</span>
                        </div>
                        <van-button
                            v-if="item.reportUrl"
                            type="info"
                            size="small"
                            round
                            @click="openReport(item.reportUrl)"
                            >剂量报告</van-button
                        >
                        <van-button
                            v-else
                            type="info"
                            size="small"
                            round
                            plain
                            @click="$toast('暂无报告')"
                            >剂量报告</van-button
                        >
                    </div>
                    <div class="info-row">
                        <span class="label">检测时间</span>
                        <span class="value">{{ item.detectionTimeText }}</span>
                    </div>
                    <div class="info-row">
                        <span class="label">检测剂量</span>
                        <span class="value">{{ item.dosageText }}</span>
                    </div>
                    <div class="info-row">
                        <span class="label">检测结果</span>
                        <span class="value">{{ item.resultContent }}</span>
                    </div>
                    <div v-if="item.createTimeText !== '—'" class="info-row subtle">
                        <span class="label">报告上传</span>
                        <span class="value">{{ item.createTimeText }}</span>
                    </div>
                    <div v-if="item.expirationTimeText !== '—'" class="info-row subtle">
                        <span class="label">到期时间</span>
                        <span class="value">{{ item.expirationTimeText }}</span>
                    </div>
                    <div v-if="item.expiredStatusText" class="info-row subtle">
                        <span class="label">是否到期</span>
                        <span class="value">{{ item.expiredStatusText }}</span>
                    </div>
                    <div v-if="item.mName" class="info-row subtle">
                        <span class="label">医疗机构</span>
                        <span class="value">{{ item.mName }}</span>
                    </div>
                    <div v-if="item.work" class="info-row subtle">
                        <span class="label">从事工作</span>
                        <span class="value">{{ item.work }}</span>
                    </div>
                    <div v-if="item.workingState" class="info-row subtle">
                        <span class="label">工作状态</span>
                        <span class="value">{{ item.workingState }}</span>
                    </div>
                </div>
            </div>
        </van-list>

        <div v-if="!loading && finished && dosageList.length === 0" class="empty-wrap">
            <van-empty description="暂无剂量记录" />
        </div>
    </div>
</template>

<script>
import moment from 'moment'
import { getPersonalDose } from '@/api/index'

export default {
    name: 'DosageReport',
    data() {
        return {
            dosageList: [],
            pageNum: 1,
            pageSize: 10,
            loading: false,
            finished: false,
            total: 0
        }
    },
    created() {
        this.loadDosageList(false)
    },
    methods: {
        onLoad() {
            this.loadDosageList(true)
        },
        loadDosageList(isLoadMore = false) {
            if (this.loading || this.finished) return

            this.loading = true
            const payload = {
                pageNum: this.pageNum,
                pageSize: this.pageSize
            }

            getPersonalDose(payload)
                .then(res => {
                    const rawList =
                        res.data?.records ||
                        res.data?.list ||
                        res.data?.rows ||
                        (Array.isArray(res.data) ? res.data : [])
                    const records = rawList.map((row, i) => this.normalizeRow(row, i))
                    const total = Number(res.data?.total ?? res.data?.totalCount ?? 0)

                    if (isLoadMore) {
                        this.dosageList = [...this.dosageList, ...records]
                    } else {
                        this.dosageList = records
                    }

                    this.total = total
                    if (this.dosageList.length >= total || records.length < this.pageSize) {
                        this.finished = true
                    } else {
                        this.pageNum += 1
                    }
                    this.loading = false
                })
                .catch(() => {
                    this.loading = false
                    this.finished = true
                })
        },
        formatDateTime(val) {
            if (val === undefined || val === null || val === '') return '—'
            const m = moment(val)
            return m.isValid() ? m.format('YYYY年MM月DD日 HH:mm') : String(val)
        },
        formatDateOnly(val) {
            if (val === undefined || val === null || val === '') return '—'
            const m = moment(val)
            return m.isValid() ? m.format('YYYY年MM月DD日') : String(val)
        },
        formatDosage(d) {
            if (d === undefined || d === null || d === '') return '—'
            const n = Number(d)
            if (!Number.isFinite(n)) return String(d)
            return n % 1 === 0 ? `${n} mSv` : `${n.toFixed(4).replace(/\.?0+$/, '')} mSv`
        },
        /** state：是否待检测，0 否、1 是；接口也可能是布尔 */
        isPending(state) {
            if (state === true || state === 1 || state === '1') return true
            if (state === false || state === 0 || state === '0') return false
            return false
        },
        normalizeRow(raw, index) {
            const id = raw.id
            const rowKey =
                id !== undefined && id !== null
                    ? `dose-${id}-${index}`
                    : `dose-${index}-${raw.detectionTime || raw.createTime || index}`

            const reportRaw = (raw.report || '').trim()
            const reportUrl = /^https?:\/\//i.test(reportRaw) ? reportRaw : ''

            return {
                rowKey,
                name: (raw.name || '—').trim() || '—',
                pending: this.isPending(raw.state),
                detectionTimeText: this.formatDateTime(raw.detectionTime),
                dosageText: this.formatDosage(raw.dosage),
                resultContent: (raw.resultContent && String(raw.resultContent).trim()) || '—',
                createTimeText: this.formatDateTime(raw.createTime),
                expirationTimeText: this.formatDateOnly(raw.expirationTime),
                expiredStatusText: (raw.expiredStatus && String(raw.expiredStatus).trim()) || '',
                mName: (raw.mName || '').trim(),
                work: (raw.work || '').trim(),
                workingState: (raw.workingState || '').trim(),
                reportUrl
            }
        },
        openReport(url) {
            if (!url) {
                this.$toast('暂无报告链接')
                return
            }
            window.location.href = url
        }
    }
};
</script>

<style scoped lang="scss">
.dosage-page {
    min-height: 100vh;
    background: #eef7f8;
    padding: 12px;
    box-sizing: border-box;
}

.list-wrap {
    padding-bottom: 8px;
}

.empty-wrap {
    padding: 48px 12px;
}

.dosage-card {
    background: #fff;
    border-radius: 12px;
    padding: 12px;
    margin-bottom: 12px;
}

.main-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.left-user {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    min-width: 0;
}

.name {
    color: #262626;
    font-size: 16px;
    font-weight: 600;
}

.pending-tag {
    font-size: 11px;
    color: #ff8a4c;
    background: rgba(255, 138, 76, 0.12);
    padding: 2px 6px;
    border-radius: 4px;
}

.info-row {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    margin-top: 6px;

    &.subtle {
        .label,
        .value {
            font-size: 13px;
        }
    }
}

.label {
    flex-shrink: 0;
    width: 72px;
    color: #b3b3b3;
    font-size: 13px;
}

.value {
    flex: 1;
    color: #333;
    font-size: 15px;
    line-height: 1.45;
    word-break: break-all;
}

.info-row.subtle .label {
    width: 72px;
}

::v-deep .van-button {
    background: #46ccdc;
    border-color: #46ccdc;
    height: 28px;
    padding: 0 14px;
    font-size: 12px;
    flex-shrink: 0;
}

::v-deep .van-button--plain {
    background: transparent;
    color: #46ccdc;
}
</style>
